import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { ActivityLogDiff } from "@/types/admin";
import {
  fieldLabel,
  formatDiffValue,
  isSuppressedDiffField,
  normalizeDiffChange,
  priorityValueLabel,
  statusValueLabel,
} from "@/features/admin/diffLabels";

/** Maximum number of diff fields rendered inline before collapsing the rest. */
const DIFF_INLINE_LIMIT = 3;

interface Props {
  /** Audit diff `{ field: [old, new] }`; null for impersonation rows. */
  diff: ActivityLogDiff | null;
  /** Audit action (create/update/delete) — drives the empty-diff rendering. */
  action: string | null;
}

/**
 * ActivityDiffCell — human-readable "label: before → after" renderer for an
 * audit row's Changes column (TASK-515).
 *
 * - create/delete (empty diff) → an em dash.
 * - empty diff on another action (e.g. redacted set-password) → "(redacted)".
 * - field names map to localized labels; values are formatted readably
 *   (booleans → yes/no, dates → locale-formatted, empties → an explicit marker).
 * - large diffs collapse behind an expand toggle.
 *
 * Rendering is safe: every value is escaped React text, never
 * `dangerouslySetInnerHTML`.
 */
export function ActivityDiffCell({ diff, action }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Drop auto-managed timestamp fields (updated_at / created_at / modified).
  // The backend already suppresses these (TASK-3678); we filter again here so
  // legacy rows never render `Updated at: … → …` noise (TASK-3679).
  const entries = diff
    ? Object.entries(diff).filter(([field]) => !isSuppressedDiffField(field))
    : [];

  if (entries.length === 0) {
    if (action === "create" || action === "delete") {
      return <span className="text-gray-400">—</span>;
    }
    return (
      <span className="italic text-gray-400">
        {t("adminPanel.activityLog.redacted")}
      </span>
    );
  }

  const visible = expanded ? entries : entries.slice(0, DIFF_INLINE_LIMIT);
  const hiddenCount = entries.length - visible.length;

  return (
    <div className="space-y-1">
      <ul className="space-y-1">
        {visible.map(([field, change]) => {
          const [oldValue, newValue] = normalizeDiffChange(change);
          // Enum-valued fields are localized by name; everything else is formatted
          // generically (TASK-3679). `status` carries lifecycle enums
          // (in_route/confirmed/…), and since TASK-4833 `priority` carries
          // low/medium/high — before that it was a number and needed no
          // translation, which is why it was not in this branch.
          // Each helper returns null for a value it does not recognise (including a
          // pre-migration numeric priority), so the generic path still runs.
          const renderValue = (value: string | null): string => {
            if (field === "status") {
              const localized = statusValueLabel(value, t);
              if (localized != null) return localized;
            }
            if (field === "priority") {
              const localized = priorityValueLabel(value, t);
              if (localized != null) return localized;
            }
            return formatDiffValue(value, t);
          };
          return (
            <li
              key={field}
              className="break-words text-xs leading-snug text-gray-700"
            >
              <span className="font-medium text-gray-900">
                {fieldLabel(field, t)}
              </span>
              {": "}
              <span className="text-gray-500">{renderValue(oldValue)}</span>
              <span className="px-1 text-gray-400">→</span>
              <span className="font-medium text-gray-700">
                {renderValue(newValue)}
              </span>
            </li>
          );
        })}
      </ul>
      {(hiddenCount > 0 || expanded) && entries.length > DIFF_INLINE_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {expanded
            ? t("adminPanel.activityLog.showLess")
            : t("adminPanel.activityLog.showMore", { count: hiddenCount })}
        </button>
      )}
    </div>
  );
}
